/**
 * Kaching Bundles & Theme Variant Picker 2-Way Synchronization
 */
(function () {
  let isSyncing = false;

  /**
   * Synchronizes theme variant selection to Kaching Bundles widget
   * @param {string} optionName - e.g. "Color", "Couleur", "Size", "Taille"
   * @param {string} optionValue - e.g. "Black", "Noir", "S", "M"
   */
  function syncThemeToKaching(optionName, optionValue) {
    if (isSyncing || !optionValue) return;

    try {
      isSyncing = true;
      const kachingContainers = document.querySelectorAll(
        '[class*="kaching"], [id*="kaching"], [data-kaching], .kaching-bundles__deal-container'
      );

      if (!kachingContainers.length) return;

      const normValue = optionValue.trim().toLowerCase();
      const normName = (optionName || '').trim().toLowerCase();

      kachingContainers.forEach((container) => {
        const selects = Array.from(container.querySelectorAll('select'));
        if (!selects.length) return;

        // Group selects by bundle row or find first item selects (#1)
        selects.forEach((select, selectIndex) => {
          // Identify if this select corresponds to the option (by option values or position)
          const matchingOption = Array.from(select.options).find((opt) => {
            const optText = opt.text.trim().toLowerCase();
            const optVal = opt.value.trim().toLowerCase();
            return optText === normValue || optVal === normValue || optText.includes(normValue);
          });

          // Only sync item #1 (first row selects) or if the select matches this option
          if (matchingOption) {
            // Check if this is one of the first selects (item #1)
            const isItem1 = selectIndex <= 1 || select.closest('[class*="item-1"], [data-item="1"], :first-child');
            if (isItem1 && select.value !== matchingOption.value) {
              select.value = matchingOption.value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              select.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        });
      });
    } catch (e) {
      console.warn('[kaching-sync] Theme -> Kaching error:', e);
    } finally {
      setTimeout(() => {
        isSyncing = false;
      }, 50);
    }
  }

  /**
   * Synchronizes Kaching Bundles selection to theme variant picker
   * @param {Event} event
   */
  function syncKachingToTheme(event) {
    if (isSyncing) return;
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const container = select.closest(
      '[class*="kaching"], [id*="kaching"], [data-kaching], .kaching-bundles__deal-container'
    );
    if (!container) return;

    try {
      isSyncing = true;
      const allSelects = Array.from(container.querySelectorAll('select'));
      const selectIndex = allSelects.indexOf(select);

      // Only sync if changing the first item in the bundle (#1)
      if (selectIndex > 1) return;

      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return;

      const selectedText = selectedOption.text.trim().toLowerCase();
      const selectedVal = selectedOption.value.trim().toLowerCase();

      const variantPicker = document.querySelector('variant-picker');
      if (!variantPicker) return;

      const inputs = Array.from(variantPicker.querySelectorAll('input[type="radio"]'));
      for (const input of inputs) {
        const inputVal = input.value.trim().toLowerCase();
        const inputLabel = (input.getAttribute('aria-label') || '').trim().toLowerCase();

        if (inputVal === selectedText || inputVal === selectedVal || inputLabel.includes(selectedText)) {
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          break;
        }
      }
    } catch (e) {
      console.warn('[kaching-sync] Kaching -> Theme error:', e);
    } finally {
      setTimeout(() => {
        isSyncing = false;
      }, 50);
    }
  }

  /**
   * Applies Bold Red styling to discounted prices in Kaching Bundles widget
   */
  function styleKachingPrices() {
    const kachingContainers = document.querySelectorAll(
      '[class*="kaching"], [id*="kaching"], [data-kaching], .kaching-bundles__deal-container'
    );

    kachingContainers.forEach((container) => {
      // Find all price elements inside deal cards/headers
      const priceContainers = container.querySelectorAll(
        '[class*="deal-price"], [class*="price-box"], [class*="deal-header"], [class*="deal-card"], [class*="pricing"]'
      );

      priceContainers.forEach((el) => {
        // Look for text nodes or spans that contain a price with strikethrough neighbor or discounted tag
        const children = Array.from(el.querySelectorAll('span, div, b, strong'));
        children.forEach((child) => {
          const isStrikethrough =
            child.tagName === 'S' ||
            child.tagName === 'DEL' ||
            child.classList.contains('kaching-bundles__deal-price--original') ||
            child.classList.contains('kaching-bundles__deal-price-original') ||
            child.closest('s, del') ||
            window.getComputedStyle(child).textDecoration.includes('line-through');

          if (!isStrikethrough) {
            const text = child.textContent.trim();
            // Check if text starts with currency symbol or has digits
            if (/^[$€£¥]?\s*\d+([.,]\d{2})?\s*[$€£¥]?$/.test(text)) {
              child.classList.add('kaching-discounted-price');
              child.style.setProperty('color', '#e60000', 'important');
              child.style.setProperty('font-weight', '800', 'important');
              child.style.setProperty('font-size', '16.5px', 'important');
            }
          }
        });
      });
    });
  }

  /**
   * Extracts mapped colors from window.__themeColorSwatches or theme variant picker swatches
   */
  function getThemeColorMap() {
    const colorMap = {};

    // 1. Read directly from global theme swatches map (populated dynamically via Liquid)
    if (window.__themeColorSwatches && typeof window.__themeColorSwatches === 'object') {
      for (const [key, val] of Object.entries(window.__themeColorSwatches)) {
        if (val) {
          colorMap[key.trim().toLowerCase()] = val.trim();
        }
      }
    }

    // 2. Supplement from theme variant picker DOM swatches
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      const labels = variantPicker.querySelectorAll('label');
      labels.forEach((label) => {
        const input = label.querySelector('input[type="radio"]');
        const swatch = label.querySelector('.swatch');
        if (input && swatch) {
          const val = (input.value || '').trim().toLowerCase();
          const bg = swatch.style.getPropertyValue('--swatch-background') || window.getComputedStyle(swatch).backgroundColor;
          if (val && bg) {
            colorMap[val] = bg.trim();
          }
        }
      });
    }

    return colorMap;
  }

  /**
   * Synchronizes color dots/swatches inside Kaching Bundles widget with theme colors
   */
  function syncKachingSwatchDots() {
    const colorMap = getThemeColorMap();
    if (!Object.keys(colorMap).length) return;

    const kachingContainers = document.querySelectorAll(
      '[class*="kaching"], [id*="kaching"], [data-kaching], .kaching-bundles__deal-container'
    );
    if (!kachingContainers.length) return;

    kachingContainers.forEach((container) => {
      // 1. Sync swatches associated with variant select dropdowns
      const selects = Array.from(container.querySelectorAll('select'));
      selects.forEach((select) => {
        const selectedOption = select.options[select.selectedIndex];
        if (!selectedOption) return;

        const optText = selectedOption.text.trim().toLowerCase();
        const optVal = selectedOption.value.trim().toLowerCase();

        let matchedColor = null;
        for (const [colorName, colorVal] of Object.entries(colorMap)) {
          if (optText === colorName || optVal === colorName || optText.includes(colorName) || optVal.includes(colorName)) {
            matchedColor = colorVal;
            break;
          }
        }

        if (matchedColor) {
          // Check select wrapper or row container
          const rowContainer = select.closest('.kaching-bundles__bar-variant, [class*="bar-variant"], [class*="variant"]') || select.parentElement;
          if (rowContainer) {
            const swatches = rowContainer.querySelectorAll(
              '.kaching-bundles__swatch, .kaching-bundles__swatches-dropdown__color, [style*="kaching-swatch-color"], [class*="swatch"], [class*="color"], [class*="dot"], [class*="circle"]'
            );
            swatches.forEach((dot) => {
              if (dot !== select && !dot.contains(select) && !select.contains(dot) && dot.children.length === 0) {
                dot.style.setProperty('background-color', matchedColor, 'important');
                dot.style.setProperty('background', matchedColor, 'important');
                dot.style.setProperty('--kaching-swatch-color', matchedColor, 'important');
              }
            });
          }
        }
      });

      // 2. Sync all explicit Kaching swatch dropdown options and button elements
      const dropdownOptions = container.querySelectorAll(
        '.kaching-bundles__swatch-dropdown__option, .kaching-bundles__swatch-buttons__button, [class*="swatch-dropdown__option"]'
      );
      dropdownOptions.forEach((optionEl) => {
        const text = (optionEl.textContent || optionEl.getAttribute('aria-label') || optionEl.getAttribute('title') || '').trim().toLowerCase();
        let matchedColor = null;
        for (const [colorName, colorVal] of Object.entries(colorMap)) {
          if (text === colorName || text.includes(colorName)) {
            matchedColor = colorVal;
            break;
          }
        }
        if (matchedColor) {
          const swatchChild = optionEl.querySelector('.kaching-bundles__swatch, .kaching-bundles__swatches-dropdown__color, [class*="color"], span, div') || optionEl;
          swatchChild.style.setProperty('background-color', matchedColor, 'important');
          swatchChild.style.setProperty('background', matchedColor, 'important');
          swatchChild.style.setProperty('--kaching-swatch-color', matchedColor, 'important');
        }
      });

      // 3. Directly target any Kaching swatch elements carrying the old base blue (#005bd3)
      const allSwatches = container.querySelectorAll('.kaching-bundles__swatch, [style*="kaching-swatch-color"]');
      allSwatches.forEach((el) => {
        const inlineStyle = el.getAttribute('style') || '';
        if (inlineStyle.toLowerCase().includes('#005bd3') || inlineStyle.toLowerCase().includes('0, 91, 211') || inlineStyle.toLowerCase().includes('0,91,211')) {
          const blueColor = colorMap['bleu'] || colorMap['blue'];
          if (blueColor) {
            el.style.setProperty('background-color', blueColor, 'important');
            el.style.setProperty('background', blueColor, 'important');
            el.style.setProperty('--kaching-swatch-color', blueColor, 'important');
          }
        }
      });
    });
  }

  // Bind event listeners
  function init() {
    // Listen to theme variant picker radio changes
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (target instanceof HTMLInputElement && target.closest('variant-picker')) {
        const optionName = target.dataset.optionName || target.name;
        const optionValue = target.value;
        syncThemeToKaching(optionName, optionValue);
        setTimeout(syncKachingSwatchDots, 30);
      }
    });

    // Listen to Kaching select changes (capture phase to catch all framework events)
    document.addEventListener('change', (e) => {
      syncKachingToTheme(e);
      setTimeout(syncKachingSwatchDots, 30);
    }, true);

    // Initial sync & price styling when Kaching bundles widget mounts asynchronously
    let initialSyncAttempts = 0;
    const initialSyncInterval = setInterval(() => {
      initialSyncAttempts++;
      const kachingContainers = document.querySelectorAll('[class*="kaching"]');
      if (kachingContainers.length > 0) {
        styleKachingPrices();
        syncKachingSwatchDots();
        const activeRadios = document.querySelectorAll('variant-picker input[type="radio"]:checked');
        activeRadios.forEach((radio) => {
          if (radio instanceof HTMLInputElement) {
            syncThemeToKaching(radio.dataset.optionName || radio.name, radio.value);
          }
        });
      }
      if (initialSyncAttempts > 30) {
        clearInterval(initialSyncInterval);
      }
    }, 300);

    // Continuous styling observer for Kaching dynamically re-rendered deals
    const observer = new MutationObserver(() => {
      styleKachingPrices();
      syncKachingSwatchDots();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
